// CActionBar — 底部動作按鈕條
// 對應 PRD §4.4 底部動作按鈕區、§9.5 動作按鈕、§10.3 觸控目標 ≥88×88
//
// 設計：
//   - 常駐按鈕：離開 / 代打（只要 barVisible=true 就在固定位置出現）
//   - 動作按鈕（吃 / 碰 / 槓 / 胡 / 放棄）：由 setChoices() 動態建立，
//     「只建立有功能的那幾顆」（沒功能的不出現，不再用灰色 disabled 佔位）。
//     吃可同時有多種牌組 → 會出現多顆「吃」按鈕（依麻將規則最多 3 顆）。
//
// 顯示流程（兩段）：
//   1) barVisible=false → 整條藏起
//   2) barVisible=true + 收合（collapsed）→ 只露出 [離開, 代打]
//   3) barVisible=true + 展開（expanded）→ 依 choices 動態生出按鈕，
//      從離開鈕同位置往左滑出、右靠 [離開] 排列（鎖在離開鈕左側依序往左）。
//   點選任一動作鈕 → 執行 handler 後觸發收合動畫。
//   點選 代打 → 由呼叫端清掉整條（不在此收合）。
//
// 迷你牌提示：吃 / 碰 / 槓 / 胡 的按鈕「正上方」會疊一排迷你牌，
//   告訴玩家此動作用到哪些牌（吃→順子三張並標記被吃那張、碰→兩張、槓→三/四張、胡→胡的那張）。
//   迷你牌資料隨 choice 一起帶入，展開時建立、收合 / 隱藏 / clear 時銷毀。
//
// === 使用圖片 ===
// - public/assets/ui/option.png（key: OPTION_KEY，5×3 spritesheet 133×133/frame）
//     row 0: 吃 / 碰 / 槓 / 聽 / 胡 normal（彩色）
//     row 2: 焦點純邊框 / 放棄 / 離開 / 代打 / 取消
//   （聽用於「出牌後聽牌」時的宣告鈕；disabled 灰色 frame 仍未使用）
// - 迷你牌透過 CTileSprite 間接使用 tile-blank-front.png + tiles-source.png + card-frame.png
//     （詳見 src/ui/tile-sprite.ts 檔頭「使用圖片」區塊）

import type Phaser from 'phaser';
import { playUIClick } from '@audio/scene-audio';

import type { TTile } from '@core/tile';
import { Suit } from '@core/types';
import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from '@config/game-config';
import { CDeferredActionDispatcher } from '@input/deferred-action-dispatcher';
import {
  CSpatialKeyboardNavigator,
  KeyboardNavigationResult,
  type IKeyboardFocusTarget,
} from '@input/spatial-keyboard-navigator';
import { OPTION_FRAME_WIDTH, OPTION_KEY, OptionFrame, type TOptionFrame } from '@scenes/boot-scene';

import { glow, reducedMotion } from './motion';
import { supportsHover } from './pointer-device';
import { CTileSprite, TileFace, TILE_BODY_HEIGHT, TILE_BODY_WIDTH } from './tile-sprite';
import { UIDepth } from './ui-depths';

/** 動作按鈕的 key。 */
export const ActionKey = {
  Chi: 'chi',
  Pong: 'pong',
  Kong: 'kong',
  Listen: 'listen',
  Hu: 'hu',
  Pass: 'pass',
  Exit: 'exit',
  AiProxy: 'aiProxy',
} as const;
export type TActionKey = (typeof ActionKey)[keyof typeof ActionKey];

/** 整體插槽順序（左 → 右）；用以推導常駐鈕位置與動作鈕的排列基準。 */
export const ACTION_KEY_ORDER: readonly TActionKey[] = [
  ActionKey.Chi,
  ActionKey.Pong,
  ActionKey.Kong,
  ActionKey.Listen,
  ActionKey.Hu,
  ActionKey.Pass,
  ActionKey.Exit,
  ActionKey.AiProxy,
];

/** 動作鈕（會被 setChoices 動態建立）由左到右的排序基準（吃碰槓聽胡放棄）。 */
const EXPANDABLE_ORDER: readonly TActionKey[] = [
  ActionKey.Chi,
  ActionKey.Pong,
  ActionKey.Kong,
  ActionKey.Listen,
  ActionKey.Hu,
  ActionKey.Pass,
];

/** 常駐按鈕（只要 barVisible=true）：離開、代打。 */
const ALWAYS_VISIBLE_KEYS: readonly TActionKey[] = [ActionKey.Exit, ActionKey.AiProxy];

/** 點代打不在此 — 代打由呼叫端清整條。其餘動作鈕點完都收合。 */
const COLLAPSE_AFTER_CLICK: ReadonlySet<TActionKey> = new Set<TActionKey>([
  ActionKey.Chi,
  ActionKey.Pong,
  ActionKey.Kong,
  ActionKey.Listen,
  ActionKey.Hu,
  ActionKey.Pass,
  ActionKey.Exit,
]);

const FRAME_NORMAL: Record<TActionKey, TOptionFrame> = {
  [ActionKey.Chi]: OptionFrame.ChiNormal,
  [ActionKey.Pong]: OptionFrame.PongNormal,
  [ActionKey.Kong]: OptionFrame.KongNormal,
  [ActionKey.Listen]: OptionFrame.ListenNormal,
  [ActionKey.Hu]: OptionFrame.HuNormal,
  [ActionKey.Pass]: OptionFrame.Pass,
  [ActionKey.Exit]: OptionFrame.Exit,
  [ActionKey.AiProxy]: OptionFrame.AiProxy,
};

/**
 * 一個動作選項（吃 / 碰 / 槓 / 胡 / 放棄）。
 * 同一個 key 可重複（多顆「吃」），各帶自己的牌組與 handler。
 */
export type TActionChoice = {
  readonly key: TActionKey;
  readonly handler: () => void;
  /** 按鈕上方迷你牌（不給 = 不顯示）。 */
  readonly tiles?: readonly TTile[];
  /** 迷你牌中要標記的那張 id（吃：被吃的捨牌；胡：胡的那張）。 */
  readonly highlightTileId?: number;
};

// ============================================================================
// 版面常數
// ============================================================================
const BUTTON_SCALE = 0.8; // 縮放後直徑 ≈ 82 px，符合 PRD §10.3 觸控目標 ≥88（含 hit area padding 後）
const MIN_BUTTON_GAP = 4; // 相鄰按鈕「至少」要有的間隙；實際 step 會被迷你牌組寬度撐大（見建構子）
const BAR_Y_OFFSET_FROM_BOTTOM = 185; // 按鈕條中心 Y — 放在玩家手牌上方
const BAR_RIGHT_EDGE_GAP = 12; // 最右鈕（代打）右緣距畫面右緣的留白 → 右靠錨點
const BAR_EDGE_MARGIN = 12; // 最左動作鈕牌組距畫面左緣的最小留白（按鈕過多時夾擠間距用）
const DEPTH_ABOVE_TILES = UIDepth.ActionBar; // 按鈕高於所有牌 sprite（牌預設 0，莊家 icon 52）
const PRESS_SCALE_RATIO = 0.9; // 按下回饋：縮一點
const EXPAND_ANIM_DURATION_MS = 220; // 展開／收合動畫時長
const ACTION_ACTIVATION_DELAY_MS = 0; // 等本次 Phaser pointerup 派送完成後再執行 action
const BUTTON_FOCUS_DEPTH = DEPTH_ABOVE_TILES + 1; // 原版 option frame 10 疊在按鈕上方

// ---- 按鈕上方迷你牌提示 ----
// scale 0.5：在「相鄰兩組 3 張牌不重疊」與「最多 8 顆按鈕仍塞進 1280」之間取的最大可行值
// （0.6 下兩者無法同時成立 → 牌組會重疊或溢出畫面）
const PREVIEW_TILE_SCALE = 0.5; // 迷你牌縮放（相對 CTileSprite 原寸 90×144）
const PREVIEW_TILE_GAP = 6; // 同一組內迷你牌的間隙（CTileSprite 原寸座標下，會一起被 PREVIEW_TILE_SCALE 縮放）
const PREVIEW_GROUP_GAP = 10; // 相鄰兩「組」迷你牌之間至少要留的水平間隙（post-scale px）；用來推導按鈕間距
const PREVIEW_DIM_ALPHA = 0.7; // 牌組中「既有手牌」的透明度；被吃 / 胡的那張維持不透明以突顯
const PREVIEW_GAP_ABOVE_BUTTON = 8; // 迷你牌底緣距按鈕頂緣的間隙（post-scale px）
const PREVIEW_FADE_MS = EXPAND_ANIM_DURATION_MS; // 迷你牌淡入時長（與展開動畫同步）
const PREVIEW_DEPTH = DEPTH_ABOVE_TILES + 1; // 迷你牌 depth 略高於按鈕，確保壓在最上層

// ---- 預覽模式 randomizePreview 用的假想牌 id 起點（負值，避免和真實牌撞 id） ----
const PREVIEW_MOCK_ID_BASE = -1000;

/**
 * 一組「n 張」迷你牌的實際顯示寬度（post-scale px）。
 * = 端到端牌心距 (n-1)*(牌寬+組內間隙)*scale + 兩端各半張 → 再加整張牌寬 scale。
 * 用於：推導按鈕 step（讓相鄰牌組不重疊）與排版計算保持同一公式。
 */
const previewGroupWidth = (tileCount: number): number =>
  tileCount <= 0
    ? 0
    : (tileCount - 1) * (TILE_BODY_WIDTH + PREVIEW_TILE_GAP) * PREVIEW_TILE_SCALE +
      TILE_BODY_WIDTH * PREVIEW_TILE_SCALE;

/** 動態建立出來的單顆動作按鈕（連同它的迷你牌容器）。 */
type TBuiltButton = {
  readonly key: TActionKey;
  readonly img: Phaser.GameObjects.Image;
  preview?: Phaser.GameObjects.Container | undefined;
};

/**
 * 底部動作按鈕條。
 * - setChoices：設定當前可用的動作（吃/碰/槓/胡/放棄），動態決定要出現哪些鈕
 * - setHandler：設定常駐鈕（離開 / 代打）的點擊回呼
 * - setVisible：切「整條」顯隱
 * - expand / collapse：切動作鈕群的展開狀態（含動畫）
 * - randomizePreview：預覽模式用，隨機模擬 0~3 顆吃 + 碰/槓/胡
 */
export class CActionBar {
  private readonly scene: Phaser.Scene;
  private readonly hoverable: boolean;
  private readonly actionDispatcher = new CDeferredActionDispatcher<Phaser.GameObjects.Image>();
  private readonly keyboardNavigator = new CSpatialKeyboardNavigator();
  private readonly focusFrames = new Map<Phaser.GameObjects.Image, Phaser.GameObjects.Image>();
  private readonly hoveredButtons = new Set<Phaser.GameObjects.Image>();
  private readonly keyboardFocusedButtons = new Set<Phaser.GameObjects.Image>();
  private keyboardFocusActive = false;

  // 常駐鈕（離開 / 代打）
  private readonly persistentButtons: Map<TActionKey, Phaser.GameObjects.Image>;
  private readonly handlers: Map<TActionKey, () => void>;
  private readonly hiddenPersistentKeys = new Set<TActionKey>();

  // 幾何
  private readonly buttonW: number; // 單顆按鈕寬（post-scale）
  private readonly exitX: number; // 離開鈕 X（固定，右靠錨點）
  private readonly aiProxyX: number; // 代打鈕 X（固定，最右）
  private readonly collapsedX: number; // 收合 / 動畫起點（= 離開鈕 X）
  private readonly centerY: number; // 按鈕中心 Y
  private readonly previewCenterY: number; // 迷你牌中心 Y（按鈕正上方）

  // 動作鈕（動態）
  private choices: readonly TActionChoice[] = [];
  private builtButtons: TBuiltButton[] = [];

  private barVisible = false; // 整條顯隱
  private expanded = false; // 動作鈕是否展開

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.hoverable = supportsHover();
    this.persistentButtons = new Map();
    this.handlers = new Map();

    const buttonW = OPTION_FRAME_WIDTH * BUTTON_SCALE;
    const buttonH = OPTION_FRAME_WIDTH * BUTTON_SCALE; // option frame 為正方形 133×133
    this.buttonW = buttonW;
    this.centerY = LOGICAL_HEIGHT - BAR_Y_OFFSET_FROM_BOTTOM;

    // 迷你牌中心 Y：按鈕頂緣再往上 (間隙 + 半張迷你牌高)
    const previewTileHalfH = (TILE_BODY_HEIGHT * PREVIEW_TILE_SCALE) / 2;
    this.previewCenterY = this.centerY - buttonH / 2 - PREVIEW_GAP_ABOVE_BUTTON - previewTileHalfH;

    // 常駐鈕（離開 / 代打）右靠畫面，彼此用「緊湊間距」（上方無牌組、不需拉開）
    const tightStep = buttonW + MIN_BUTTON_GAP;
    this.aiProxyX = LOGICAL_WIDTH - BAR_RIGHT_EDGE_GAP - buttonW / 2;
    this.exitX = this.aiProxyX - tightStep;
    this.collapsedX = this.exitX;

    const persistentX: Record<string, number> = {
      [ActionKey.Exit]: this.exitX,
      [ActionKey.AiProxy]: this.aiProxyX,
    };
    for (const key of ALWAYS_VISIBLE_KEYS) {
      const img = this.createButtonImage(key, persistentX[key]!);
      this.attachInteractions(img, key, () => {
        if (COLLAPSE_AFTER_CLICK.has(key) && this.expanded) this.collapse();
        this.handlers.get(key)?.();
      });
      this.persistentButtons.set(key, img);
    }
  }

  /** 一顆按鈕的「半寬」用於間距計算：有牌組時取牌組半寬，否則取按鈕半寬。 */
  private halfExtent(tiles?: readonly TTile[]): number {
    const pw = tiles && tiles.length > 0 ? previewGroupWidth(tiles.length) : 0;
    return Math.max(this.buttonW / 2, pw / 2);
  }

  /** 建立一顆按鈕 Image（共用樣式），預設藏起。 */
  private createButtonImage(key: TActionKey, x: number): Phaser.GameObjects.Image {
    const img = this.scene.add
      .image(x, this.centerY, OPTION_KEY, FRAME_NORMAL[key])
      .setScale(BUTTON_SCALE)
      .setDepth(DEPTH_ABOVE_TILES)
      .setVisible(false)
      .setInteractive({ useHandCursor: true });
    const focusFrame = this.scene.add
      .image(x, this.centerY, OPTION_KEY, OptionFrame.Focus)
      .setScale(BUTTON_SCALE)
      .setDepth(BUTTON_FOCUS_DEPTH)
      .setVisible(false);
    this.focusFrames.set(img, focusFrame);
    return img;
  }

  /** 掛上按下回饋 / 原版純邊框 / 點擊回呼。 */
  private attachInteractions(
    img: Phaser.GameObjects.Image,
    _key: TActionKey,
    onClick: () => void
  ): void {
    img.on('pointerdown', () => this.setButtonScale(img, BUTTON_SCALE * PRESS_SCALE_RATIO));
    img.on('pointerup', () => {
      this.setButtonScale(img, BUTTON_SCALE);
      playUIClick(this.scene);
      this.actionDispatcher.dispatch(
        img,
        (callback) => {
          this.scene.time.delayedCall(ACTION_ACTIVATION_DELAY_MS, callback);
        },
        onClick
      );
    });
    img.on('pointerout', () => {
      this.setButtonScale(img, BUTTON_SCALE);
      if (this.hoverable) {
        this.hoveredButtons.delete(img);
        this.syncButtonFocusFrame(img);
      }
    });
    if (this.hoverable) {
      img.on('pointerover', () => {
        this.hoveredButtons.add(img);
        this.syncButtonFocusFrame(img);
      });
    }
  }

  // --------------------------------------------------------------------------
  // 顯隱（整條）
  // --------------------------------------------------------------------------

  /**
   * 整條顯隱。
   * - true  → 常駐鈕（離開、代打）顯示；動作鈕維持當前狀態（預設仍收合）
   * - false → 全藏；銷毀動態動作鈕、reset expanded=false
   */
  setVisible(visible: boolean): void {
    this.barVisible = visible;
    if (visible) {
      for (const key of ALWAYS_VISIBLE_KEYS) {
        this.setButtonVisible(
          this.persistentButtons.get(key)!,
          !this.hiddenPersistentKeys.has(key)
        );
      }
      this.rebuildKeyboardTargets();
    } else {
      this.keyboardFocusActive = false;
      this.keyboardNavigator.clear();
      for (const img of this.persistentButtons.values()) {
        this.scene.tweens.killTweensOf(img);
        const focusFrame = this.focusFrames.get(img);
        if (focusFrame) this.scene.tweens.killTweensOf(focusFrame);
        this.setButtonVisible(img, false);
      }
      this.destroyBuiltButtons();
      this.expanded = false;
    }
  }

  isVisible(): boolean {
    return this.barVisible;
  }

  /** 單獨切換離開／代打常駐鈕；整條隱藏時只記狀態，不提前顯示。 */
  setPersistentVisible(key: TActionKey, visible: boolean): void {
    if (!ALWAYS_VISIBLE_KEYS.includes(key)) return;
    if (visible) this.hiddenPersistentKeys.delete(key);
    else this.hiddenPersistentKeys.add(key);
    const img = this.persistentButtons.get(key);
    if (img) this.setButtonVisible(img, this.barVisible && visible);
    this.rebuildKeyboardTargets();
  }

  // --------------------------------------------------------------------------
  // 展開 / 收合（動作鈕群）
  // --------------------------------------------------------------------------

  /** 展開：依 choices 動態生出動作鈕，從離開鈕位置往左滑出、右靠離開鈕排列。 */
  expand(): void {
    if (!this.barVisible) return;
    if (this.expanded) return;
    this.expanded = true;
    this.buildExpandable();
  }

  /** 收合：動作鈕 tween 回離開鈕位置，動畫結束後銷毀。 */
  collapse(): void {
    if (!this.expanded) return;
    this.expanded = false;
    const built = this.builtButtons;
    this.builtButtons = [];
    this.rebuildKeyboardTargets();
    for (const b of built) {
      this.destroyPreview(b);
      this.scene.tweens.killTweensOf(b.img);
      const focusFrame = this.focusFrames.get(b.img);
      if (focusFrame) this.scene.tweens.killTweensOf(focusFrame);
      this.scene.tweens.add({
        targets: focusFrame ? [b.img, focusFrame] : b.img,
        x: this.collapsedX,
        duration: reducedMotion() ? 0 : EXPAND_ANIM_DURATION_MS,
        ease: 'Cubic.easeIn',
        onComplete: () => this.destroyButton(b.img),
      });
    }
  }

  isExpanded(): boolean {
    return this.expanded;
  }

  /** 依 this.choices 從頭建立所有動作鈕（會先銷毀舊的）。 */
  private buildExpandable(): void {
    this.destroyBuiltButtons();

    // 依固定順序排序（吃在最左、放棄在最右），同 key（多顆吃）維持原順序
    const sorted = [...this.choices].sort(
      (a, b) => EXPANDABLE_ORDER.indexOf(a.key) - EXPANDABLE_ORDER.indexOf(b.key)
    );
    const n = sorted.length;
    if (n === 0) return;

    // 逐對計算「中心距」：只有上方有牌組的按鈕之間才需要拉開到容得下牌組；
    // 兩顆都沒牌組（如 放棄 / 離開 / 代打）就用緊湊間距。
    // 動作鈕右靠「離開」鈕（collapsedX）往左排：rightExtent 從離開鈕（無牌組）起算。
    const tightStep = this.buttonW + MIN_BUTTON_GAP;
    const gaps: number[] = new Array(n); // gaps[i] = 第 i 顆到「右鄰」的中心距
    let rightExtent = this.buttonW / 2; // 右鄰起始 = 離開鈕半寬
    let rightHasPreview = false;
    for (let i = n - 1; i >= 0; i--) {
      const c = sorted[i]!;
      const hasPreview = !!(c.tiles && c.tiles.length > 0);
      const myExtent = this.halfExtent(c.tiles);
      const spacingGap = hasPreview || rightHasPreview ? PREVIEW_GROUP_GAP : MIN_BUTTON_GAP;
      gaps[i] = Math.max(tightStep, myExtent + rightExtent + spacingGap);
      rightExtent = myExtent;
      rightHasPreview = hasPreview;
    }

    // 從右到左累加得到各鈕 X（右靠 collapsedX）。若最左牌組頂到左邊界，
    // 等比壓縮所有 gap 使其剛好塞下（極端數量才會發生，僅該次輕微靠近、不切出畫面）。
    let leftmostX = this.collapsedX;
    let acc = 0;
    for (let i = n - 1; i >= 0; i--) {
      acc += gaps[i]!;
      leftmostX = this.collapsedX - acc;
    }
    const leftLimit = BAR_EDGE_MARGIN + this.halfExtent(sorted[0]!.tiles);
    const compress = leftmostX < leftLimit ? (this.collapsedX - leftLimit) / acc : 1;

    let x = this.collapsedX;
    const xs: number[] = new Array(n);
    for (let i = n - 1; i >= 0; i--) {
      x -= gaps[i]! * compress;
      xs[i] = x;
    }

    sorted.forEach((choice, i) => {
      const finalX = xs[i]!;
      const img = this.createButtonImage(choice.key, this.collapsedX).setVisible(true);
      this.attachInteractions(img, choice.key, () => {
        // 此處已離開原 pointerup 派送，可安全銷毀剛按下的 Phaser Image。
        // 必須先退場舊 choices 再送出 action；handler 會同步發布下一回合並重建 action bar，
        // 若在 handler 後才 collapse，會誤收合剛建立的新一代按鈕。
        this.expanded = false;
        this.destroyBuiltButtons();
        choice.handler();
      });
      this.scene.tweens.add({
        targets: [img, this.focusFrames.get(img)!],
        x: finalX,
        duration: reducedMotion() ? 0 : EXPAND_ANIM_DURATION_MS,
        ease: 'Cubic.easeOut',
        onComplete: () => {
          if (img.scene && this.expanded)
            glow(this.scene, finalX, img.y, img.displayWidth, img.displayHeight)?.setDepth(
              img.depth + 1
            );
        },
      });

      const built: TBuiltButton = { key: choice.key, img };
      if (choice.tiles && choice.tiles.length > 0) {
        built.preview = this.buildPreview(choice.tiles, choice.highlightTileId, finalX);
      }
      this.builtButtons.push(built);
    });
    this.rebuildKeyboardTargets();
  }

  /** 銷毀所有動態動作鈕（連同迷你牌），立即生效（無動畫）。 */
  private destroyBuiltButtons(): void {
    this.keyboardNavigator.clear();
    for (const b of this.builtButtons) {
      this.destroyPreview(b);
      this.destroyButton(b.img);
    }
    this.builtButtons = [];
  }

  /** Action Bar 的左右方向鍵與確認鍵；回傳 true 表示事件已處理。 */
  handleKeyboard(event: KeyboardEvent): boolean {
    if (!this.barVisible) return false;
    if (!this.keyboardFocusActive) this.focusKeyboard();
    return this.keyboardNavigator.handleKey(event.key) !== KeyboardNavigationResult.Ignored;
  }

  /** 讓 Action Bar 取得鍵盤焦點；expanded 時優先聚焦第一顆動作鈕。 */
  focusKeyboard(): void {
    this.keyboardFocusActive = true;
    this.rebuildKeyboardTargets();
  }

  clearKeyboardFocus(): void {
    this.keyboardFocusActive = false;
    this.keyboardNavigator.clear();
  }

  private rebuildKeyboardTargets(): void {
    if (!this.barVisible || !this.keyboardFocusActive) {
      this.keyboardNavigator.clear();
      return;
    }
    const images = [
      ...this.builtButtons.map((button) => button.img),
      ...ALWAYS_VISIBLE_KEYS.map((key) => this.persistentButtons.get(key)!).filter(
        (image) => image.visible
      ),
    ].filter((image) => image.visible && image.active);
    images.sort((left, right) => left.x - right.x);
    const targets: IKeyboardFocusTarget[] = images.map((image) => ({
      x: image.x,
      y: image.y,
      activate: () => image.emit('pointerup'),
      setFocused: (focused) => {
        if (focused) this.keyboardFocusedButtons.add(image);
        else this.keyboardFocusedButtons.delete(image);
        this.syncButtonFocusFrame(image);
      },
    }));
    this.keyboardNavigator.setTargets(targets);
  }

  // --------------------------------------------------------------------------
  // 設定 choices / handler
  // --------------------------------------------------------------------------

  /**
   * 設定當前可用的動作選項（吃 / 碰 / 槓 / 胡 / 放棄）。
   * 只會建立傳入的這些（沒功能的不出現）；同 key 可重複（多顆吃）。
   * 若呼叫時已展開，會即時重建。
   */
  setChoices(choices: readonly TActionChoice[]): void {
    this.choices = choices;
    if (this.expanded) this.buildExpandable();
  }

  /** 設定常駐鈕（離開 / 代打）的點擊回呼；傳 undefined = 清掉。 */
  setHandler(key: TActionKey, handler: (() => void) | undefined): void {
    if (handler) this.handlers.set(key, handler);
    else this.handlers.delete(key);
  }

  /** 清空 choices + 常駐 handler + 整條隱藏（回合切換時 reset 用，無動畫）。 */
  clear(): void {
    this.choices = [];
    this.handlers.clear();
    this.setVisible(false); // 連帶銷毀動態鈕
  }

  /**
   * 預覽模式專用：隨機模擬 0~3 顆「吃」加上隨機的碰 / 槓 / 胡 + 放棄，
   * 用來驗收「多顆吃」與「只顯示有功能按鈕」的視覺。
   */
  randomizePreview(): void {
    const choices: TActionChoice[] = [];
    let id = PREVIEW_MOCK_ID_BASE;
    const t = (suit: TTile['suit'], rank: number): TTile => ({ id: id--, suit, rank });

    // 0~3 顆吃：每顆給一組連續順子，標記中間那張
    const chiCount = Math.floor(Math.random() * 4);
    for (let c = 0; c < chiCount; c++) {
      const base = 1 + c; // 索 base..base+2
      const seq: TTile[] = [t(Suit.Sou, base), t(Suit.Sou, base + 1), t(Suit.Sou, base + 2)];
      choices.push({
        key: ActionKey.Chi,
        handler: () => {},
        tiles: seq,
        highlightTileId: seq[1]!.id,
      });
    }
    if (Math.random() < 0.5) {
      // 碰：手上兩張 + 被碰的捨牌（標記）
      const pongWin = t(Suit.Dragon, 1);
      choices.push({
        key: ActionKey.Pong,
        handler: () => {},
        tiles: [t(Suit.Dragon, 1), t(Suit.Dragon, 1), pongWin],
        highlightTileId: pongWin.id,
      });
    }
    if (Math.random() < 0.5) {
      // 明槓：手上三張 + 被槓的捨牌（標記）
      const kongWin = t(Suit.Wind, 1);
      choices.push({
        key: ActionKey.Kong,
        handler: () => {},
        tiles: [t(Suit.Wind, 1), t(Suit.Wind, 1), t(Suit.Wind, 1), kongWin],
        highlightTileId: kongWin.id,
      });
    }
    if (Math.random() < 0.5) {
      const win = t(Suit.Pin, 8);
      choices.push({ key: ActionKey.Hu, handler: () => {}, tiles: [win], highlightTileId: win.id });
    }
    choices.push({ key: ActionKey.Pass, handler: () => {} });

    this.setChoices(choices);
  }

  // --------------------------------------------------------------------------
  // hover / 迷你牌
  // --------------------------------------------------------------------------

  /** 讓按鈕與其原版純邊框保持相同縮放。 */
  private setButtonScale(img: Phaser.GameObjects.Image, scale: number): void {
    img.setScale(scale);
    this.focusFrames.get(img)?.setScale(scale);
  }

  /** 同步按鈕顯示狀態；邊框只在 hover 或鍵盤焦點時顯示。 */
  private setButtonVisible(img: Phaser.GameObjects.Image, visible: boolean): void {
    img.setVisible(visible);
    this.syncButtonFocusFrame(img);
  }

  /** 原版 BaseOptionButton.focus()：疊上 option frame 10，不使用 tint 或程式光暈。 */
  private syncButtonFocusFrame(img: Phaser.GameObjects.Image): void {
    const focusFrame = this.focusFrames.get(img);
    if (!focusFrame) return;
    const focused = this.hoveredButtons.has(img) || this.keyboardFocusedButtons.has(img);
    focusFrame.setVisible(img.visible && focused);
  }

  /** 銷毀按鈕與一對一的原版純邊框。 */
  private destroyButton(img: Phaser.GameObjects.Image): void {
    this.scene.tweens.killTweensOf(img);
    const focusFrame = this.focusFrames.get(img);
    if (focusFrame) {
      this.scene.tweens.killTweensOf(focusFrame);
      focusFrame.destroy();
    }
    this.focusFrames.delete(img);
    this.hoveredButtons.delete(img);
    this.keyboardFocusedButtons.delete(img);
    img.destroy();
  }

  /** 在 (centerX, previewCenterY) 建立一排迷你牌容器並淡入。 */
  private buildPreview(
    tiles: readonly TTile[],
    highlightId: number | undefined,
    centerX: number
  ): Phaser.GameObjects.Container {
    const step = TILE_BODY_WIDTH + PREVIEW_TILE_GAP; // 未縮放座標下的相鄰牌中心距
    const container = this.scene.add
      .container(centerX, this.previewCenterY)
      .setDepth(PREVIEW_DEPTH)
      .setScale(PREVIEW_TILE_SCALE)
      .setAlpha(0);

    tiles.forEach((tile, i) => {
      const localX = (i - (tiles.length - 1) / 2) * step;
      const sprite = new CTileSprite(this.scene, localX, 0, tile, TileFace.HandBottom);
      // 有指定 highlight 時：被吃 / 胡的那張維持不透明，其餘「既有手牌」帶透明度，
      // 一眼看出哪張是準備吃 / 胡的、哪些是手上原有的（不再用黃色邊框）。
      if (highlightId !== undefined && tile.id !== highlightId) {
        sprite.setAlpha(PREVIEW_DIM_ALPHA);
      }
      container.add(sprite);
    });

    this.scene.tweens.add({ targets: container, alpha: 1, duration: PREVIEW_FADE_MS });
    return container;
  }

  /** 銷毀某按鈕的迷你牌容器（連同子 CTileSprite）。 */
  private destroyPreview(b: TBuiltButton): void {
    if (b.preview) {
      this.scene.tweens.killTweensOf(b.preview);
      b.preview.destroy(true); // true → 一併銷毀子物件
      b.preview = undefined;
    }
  }
}
